import React, { useLayoutEffect, useContext, useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Brand from '../Brand/Brand';
import Navigation, { NavigationLine } from '../Navigation/Navigation';
import User from '../User/User';
import { componentsMenu, dashboardMenu, demoPages } from '../../menu';
import ThemeContext from '../../contexts/themeContext';
import Card, { CardBody } from '../../components/bootstrap/Card';

import Hand from '../../assets/img/hand.png';
import HandWebp from '../../assets/img/hand.webp';
import Icon from '../../components/icon/Icon';
import Button from '../../components/bootstrap/Button';
import Tooltips from '../../components/bootstrap/Tooltips';
import useDarkMode from '../../hooks/useDarkMode';
import useAsideTouch from '../../hooks/useAsideTouch';
import { userAuth } from '../../contexts/userContext';
import { useLocation, useNavigate } from 'react-router-dom';
import apiServicewt from '../../api/apiServicewt';
import { useDueDate } from '../../contexts/dueDateContext';
import { useLoginSettings } from '../../contexts/loginSettingsContext';

const admindata = demoPages['Manage_sadmin'];

const admincontroll = (pagers = {}, isadmin) => {
	const temppage = { ...pagers };
	if (isadmin) {
		return temppage;
	} else {
		delete temppage['servicePages'];
		delete temppage['Transaction'];
		delete temppage['Manage_report'];
		delete temppage['Manage_v'];
		delete temppage['Manage_roles'];
		delete temppage['Manage_super'];
		delete temppage['Orders_v'];
		delete temppage['addProduct'];
		delete temppage['Manage_u'];
		delete temppage['Manage_refunds'];
		delete temppage['Manage_e'];
		delete temppage['vbookingPages'];
		delete temppage['venderSetting'];
		delete temppage['branchList'];
		delete temppage['sliderSetting'];
		delete temppage['Calendar_v'];
		return temppage;
	}
};

const shouldHideRestrictedPages = ({ dueDateStatus, errorCode }) =>
	dueDateStatus === 'INVALID' || errorCode === 'subscription_status';

const applyInvalidDueDateRestrictions = (pages = {}, restrictionState) => {
	if (!shouldHideRestrictedPages(restrictionState)) {
		return pages;
	}

	return {
		...pages,
		add_promo: {
			...pages.add_promo,
			hide: true,
		},
	};
};

const Aside = () => {
	const { asideStatus, setAsideStatus } = useContext(ThemeContext);
	const { asideStyle, touchStatus, hasTouchButton, asideWidthWithSpace, x } = useAsideTouch();
	const isModernDesign = process.env.REACT_APP_MODERN_DESGIN === 'true';
	const constraintsRef = useRef(null);
	const [doc, setDoc] = useState(false);
	const { darkModeStatus } = useDarkMode();
	const startX = useRef(null);
	const endX = useRef(null);
	// Keep all responsive behaviour in sync with the app-wide mobile breakpoint
	// If the env var is missing, fall back to a sensible default (992px)
	const MOBILE_BREAKPOINT = Number(process.env.REACT_APP_MOBILE_BREAKPOINT_SIZE || 992);
	const deviceWidth = window.innerWidth;
	const { token, setToken } = useContext(userAuth);
	const location = useLocation();
	const { loginSettings } = useLoginSettings();
	const [faviconError, setFaviconError] = useState(false);

	const [showOverlay, setShowOverlay] = useState(false);

	const navigate = useNavigate();

	const { reqStatus, dueDateStatus, errorCode } = useDueDate();

	useEffect(() => {
		if (reqStatus === 'error') {
			const keysToDelete = [
				'/appointment/pending',
				'/appointment/completed',
				'/appointment/expired',
				'/appointment/cancelled',
				'/orders',
				'/services/list',
				'/services/category',
				'/services/promotions',
				'/viewproducts',
				'/sliders',
				'/employee/list',
				'/employee/shifts',
				'/employee/worker-schedule',
				'/employee/leave-requests',
				'/customers',
				'/admins',
				'/roles',
				'/transaction/payroll',
				'/transaction/income',
				'/transaction/expences',
				'/transaction/cheque',
				'/transaction/extype',
				'/reports/orders',
				'/reports/appointments',
				'/reports/products',
				'/reports/commission',
				'/reports/payments',
				'/reports/schedule',
				'/reports/attendance',
				'/reports/pending-refund',
				'/reports/completed-refund',
				'/refunds/requested',
				'/refunds/completed',
				'/branch',
				'/settings',
			];
			if (keysToDelete.includes(location.pathname)) {
				navigate('/dashboard');
			}
			setShowOverlay(false);
		} else {
			setShowOverlay(true);
		}
	}, [navigate, reqStatus, location.pathname]);

	useEffect(() => {
		const invalidRestrictedPaths = [
			'/services/promotions',
		];

		if (
			shouldHideRestrictedPages({ dueDateStatus, errorCode }) &&
			invalidRestrictedPaths.includes(location.pathname)
		) {
			navigate('/dashboard');
		}
	}, [dueDateStatus, errorCode, location.pathname, navigate]);

	useLayoutEffect(() => {
		function handleTouchStart(e) {
			startX.current = e.touches[0].clientX;
		}

		function handleTouchEnd(e) {
			endX.current = e.changedTouches[0].clientX;
			const diffX = endX.current - startX.current;

			// Use the same breakpoint as the Header to decide when the aside should behave like a drawer
			const isMobileOrTab = deviceWidth <= MOBILE_BREAKPOINT;

			if (isMobileOrTab && diffX < -80) {
				setAsideStatus(false);
			}
		}

		if (asideStatus) {
			document.addEventListener('touchstart', handleTouchStart);
			document.addEventListener('touchend', handleTouchEnd);
		}

		return () => {
			document.removeEventListener('touchstart', handleTouchStart);
			document.removeEventListener('touchend', handleTouchEnd);
		};
	}, [asideStatus]);

	// Reset favicon error state when URL changes
	useEffect(() => {
		setFaviconError(false);
	}, [loginSettings?.fav_icon]);

	return (
		<>
			<motion.aside
				style={asideStyle}
				ref={constraintsRef}
				initial={{ x: 0 }}
				animate={{ x: deviceWidth <= MOBILE_BREAKPOINT ? (asideStatus ? 0 : '-100%') : 0 }}
				transition={{ duration: 0.1, ease: 'easeInOut' }}
				className={classNames(
					'aside',
					//{ open: true },
					{ open: asideStatus },
					{
						//'aside-touch-bar': true,
						//'aside-touch-bar-close': true,
						//'aside-touch-bar-open': true,
						'aside-touch-bar': hasTouchButton && isModernDesign,
						'aside-touch-bar-close': !touchStatus && hasTouchButton && isModernDesign,
						'aside-touch-bar-open': touchStatus && hasTouchButton && isModernDesign,
					},
				)}>
				<div className='aside-head'>
					{/* Show favicon above expand icon when aside is collapsed */}
					{!asideStatus && loginSettings?.fav_icon && !faviconError && (
						<div
							className='aside-favicon-wrapper'
							style={{
								display: 'flex',
								justifyContent: 'center',
								marginBottom: 10,
								marginTop: 10,
							}}>
							<img
								src={loginSettings.fav_icon}
								alt='Site favicon'
								onError={() => setFaviconError(true)}
								style={{ width: 48, height: 48, objectFit: 'contain' }}
							/>
						</div>
					)}
					<Brand asideStatus={asideStatus} setAsideStatus={setAsideStatus} />
				</div>
				<div className='aside-body'>
					<Navigation
						menu={dashboardMenu}
						id='aside-dashboard'
						asideStatus={asideStatus}
						setAsideStatus={setAsideStatus}
					/>
					{!doc && (
						<>
							<NavigationLine />
							<Navigation
								menu={applyInvalidDueDateRestrictions(
									admincontroll(demoPages, showOverlay),
									{ dueDateStatus, errorCode },
								)}
								id='aside-demo-pages'
								asideStatus={asideStatus}
								setAsideStatus={setAsideStatus}
							/>
							<NavigationLine />
						</>
					)}

					{doc && (
						<>
							<NavigationLine />
							<Navigation
								menu={componentsMenu}
								id='aside-menu-two'
								asideStatus={asideStatus}
								setAsideStatus={setAsideStatus}
							/>
							<NavigationLine />
						</>
					)}

					{asideStatus && doc && (
						<Card className='m-3 '>
							<CardBody className='pt-0'>
								<img
									srcSet={HandWebp}
									src={Hand}
									alt='Hand'
									width={130}
									height={130}
								/>
								<p
									className={classNames('h4', {
										'text-dark': !darkModeStatus,
										'text-light': darkModeStatus,
									})}>
									{'Everything is ready!'}
								</p>
								<Button
									color='info'
									isLight
									className='w-100'
									onClick={() => setDoc(false)}>
									{'Demo Pages'}
								</Button>
							</CardBody>
						</Card>
					)}
				</div>
			</motion.aside>
		</>
	);
};

export default Aside;
